import { type ReactElement } from 'react';
import { ShieldCheck, Trash2, UserCheck, UserX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTablePill,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';

import type { AdminUserViewModel } from '../api';

const ACTIVE_STATUSES: ReadonlySet<string> = new Set(['ACTIVE', 'active', 'Active']);

/**
 * Locked row + header heights so the scroll container in `AdminUsersPage`
 * can size itself to exactly N rows. The `h-[Npx] py-0` classes must be
 * written as string literals (Tailwind JIT scans source for full class
 * names) — keep these in sync if the constants change.
 */
export const ADMIN_USERS_ROW_HEIGHT_PX = 40;
export const ADMIN_USERS_HEAD_HEIGHT_PX = 40;
const COMPACT_CELL_CLASS = 'h-[40px] py-0';
const COMPACT_HEAD_CLASS = 'h-[40px] py-0';
// Zebra: every-other-row tint, strong enough to scan on a light page
// without competing with the row hover state.
const ZEBRA_ROW_CLASS = 'even:bg-muted/55';
// Compact pill — ~30% shorter than the default `DataTablePill`
// (`px-2.5 py-0.5 text-xs`) so role/status chips don't dominate the row.
const COMPACT_PILL_CLASS = 'px-1.5 py-0 text-[10px] leading-[14px] sm:text-[10px]';

export function AdminUsersTable({
  users,
  emptyCell,
  onDelete,
  pendingDeleteUuid,
  currentUserUuid,
  userHref,
  onManageRoles,
  onDeactivate,
  onActivate,
  pendingActionUuid = null,
}: {
  readonly users: readonly AdminUserViewModel[];
  readonly emptyCell: string;
  readonly onDelete: (user: AdminUserViewModel) => void;
  readonly pendingDeleteUuid: string | null;
  /** UUID of the signed-in user — that row's delete/deactivate buttons are disabled. */
  readonly currentUserUuid: string | null;
  /** When provided, the username becomes a link to this href (e.g. a per-user detail page). */
  readonly userHref?: (uuid: string) => string;
  /** When provided, a "manage roles" action is shown per row. */
  readonly onManageRoles?: (user: AdminUserViewModel) => void;
  /** When provided (with {@link onActivate}), a deactivate/activate action is shown per row. */
  readonly onDeactivate?: (user: AdminUserViewModel) => void;
  readonly onActivate?: (user: AdminUserViewModel) => void;
  /** UUID whose role/status action is in flight (spinner). */
  readonly pendingActionUuid?: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const showStatusActions = onDeactivate !== undefined && onActivate !== undefined;
  const hasExtraActions = onManageRoles !== undefined || showStatusActions;
  const actionsWidthClass = hasExtraActions ? 'w-[12%] min-w-[7rem]' : 'w-[6%]';
  return (
    <DataTableTable aria-label={t('routes.admin.users.listAriaLabel')}>
      <DataTableThead className="sticky top-0 z-10 bg-card">
        <DataTableHeaderRow>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[16%] min-w-[7rem]">
            {t('routes.admin.users.table.username')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[14%] min-w-[6rem]">
            {t('routes.admin.users.table.firstName')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[14%] min-w-[6rem]">
            {t('routes.admin.users.table.lastName')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[24%] min-w-[10rem]">
            {t('routes.admin.users.table.email')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[18%] min-w-[8rem]">
            {t('routes.admin.users.table.roles')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[8%] min-w-[5rem]">
            {t('routes.admin.users.table.status')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName={actionsWidthClass} align="right">
            <span className="sr-only">{t('routes.admin.users.table.actions')}</span>
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {users.map((u) => {
          const displayName = u.fullName.length > 0
            ? u.fullName
            : u.name.length > 0
              ? u.name
              : u.email.length > 0
                ? u.email
                : t('routes.admin.users.unknownUser');
          const isPending = pendingDeleteUuid === u.uuid;
          const isActive = u.status !== null && ACTIVE_STATUSES.has(u.status);
          const isSelf = currentUserUuid !== null && u.uuid === currentUserUuid;
          const deleteDisabled = isPending || u.uuid.length === 0 || isSelf;
          const deleteLabel = isSelf
            ? t('routes.admin.users.deleteSelfDisabled')
            : t('routes.admin.users.deleteAction', { name: displayName });
          return (
            <DataTableTr
              key={u.uuid.length > 0 ? u.uuid : `row-${u.email}`}
              className={ZEBRA_ROW_CLASS}
            >
              <DataTableTd className={`${COMPACT_CELL_CLASS} pr-1 font-medium text-foreground`} dataOffset={false}>
                {u.name.length > 0 && userHref !== undefined && u.uuid.length > 0 ? (
                  <Link
                    to={userHref(u.uuid)}
                    className="text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 truncate inline-block max-w-full"
                  >
                    {u.name}
                  </Link>
                ) : (
                  <span className="truncate">{u.name.length > 0 ? u.name : emptyCell}</span>
                )}
              </DataTableTd>
              <DataTableTd className={`${COMPACT_CELL_CLASS} text-foreground/90`} dataOffset>
                {u.firstName.length > 0 ? u.firstName : emptyCell}
              </DataTableTd>
              <DataTableTd className={`${COMPACT_CELL_CLASS} text-foreground/90`} dataOffset>
                {u.lastName.length > 0 ? u.lastName : emptyCell}
              </DataTableTd>
              <DataTableTd className={`${COMPACT_CELL_CLASS} text-foreground/90 truncate`} dataOffset>
                {u.email.length > 0 ? u.email : emptyCell}
              </DataTableTd>
              <DataTableTd className={COMPACT_CELL_CLASS} dataOffset>
                {u.roles.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {u.roles.map((role) => (
                      <DataTablePill
                        key={role}
                        className={`border-muted-foreground/20 bg-muted/60 text-foreground/80 ${COMPACT_PILL_CLASS}`}
                      >
                        {role}
                      </DataTablePill>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground text-xs">{emptyCell}</span>
                )}
              </DataTableTd>
              <DataTableTd className={COMPACT_CELL_CLASS} dataOffset>
                {u.status !== null ? (
                  isActive ? (
                    <DataTablePill className={COMPACT_PILL_CLASS}>{u.status}</DataTablePill>
                  ) : (
                    <DataTablePill className={`border-muted-foreground/30 bg-muted text-muted-foreground ${COMPACT_PILL_CLASS}`}>
                      {u.status}
                    </DataTablePill>
                  )
                ) : (
                  <span className="text-muted-foreground text-xs">{emptyCell}</span>
                )}
              </DataTableTd>
              <DataTableTd className={COMPACT_CELL_CLASS} align="right" dataOffset>
                <div className="flex items-center justify-end gap-1">
                  {onManageRoles !== undefined && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        onManageRoles(u);
                      }}
                      disabled={u.uuid.length === 0}
                      aria-label={t('routes.admin.users.manageRolesAction', { name: displayName })}
                      title={t('routes.admin.users.manageRolesAction', { name: displayName })}
                    >
                      <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                  {showStatusActions &&
                    (isActive ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={() => {
                          onDeactivate(u);
                        }}
                        disabled={isSelf || u.uuid.length === 0}
                        loading={pendingActionUuid === u.uuid}
                        aria-label={
                          isSelf
                            ? t('routes.admin.users.deactivateSelfDisabled')
                            : t('routes.admin.users.deactivateAction', { name: displayName })
                        }
                        title={isSelf ? t('routes.admin.users.deactivateSelfDisabled') : undefined}
                      >
                        <UserX className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          onActivate(u);
                        }}
                        disabled={u.uuid.length === 0}
                        loading={pendingActionUuid === u.uuid}
                        aria-label={t('routes.admin.users.activateAction', { name: displayName })}
                        title={t('routes.admin.users.activateAction', { name: displayName })}
                      >
                        <UserCheck className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    ))}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    onClick={() => {
                      onDelete(u);
                    }}
                    disabled={deleteDisabled}
                    aria-label={deleteLabel}
                    title={isSelf ? deleteLabel : undefined}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </DataTableTd>
            </DataTableTr>
          );
        })}
      </DataTableTbody>
    </DataTableTable>
  );
}
